import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Typography, FormControlLabel, Switch, } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import CommonTable from "../../../common_components/CommonTable";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DateRangeTabs from "./DateRangeTabs";
import { EXPAND_TRIGGERS } from "../../../common_components/tableConstants";
import CustomDatePicker from "../../../common_components/daterangepicker/CustomDatePicker";
import ClientStatsGrid from "./ClientStatsGrid";
import { useClientRowState } from "./useClientRowState";
import SportPerformance from "./SportPerformance";
import VisibleClientsDropdown from "../../../common_components/VisibleClientsDropdown";
import AnalyticsLineChart from "../../../common_components/AnalyticsLineChart/AnalyticsLineChart";
import FiltersRow from "./FiltersRow";
import { formatStat } from "../../../helper/statsFormatter";
import { StyledCurrency } from "../../../helper/StyledCurrency";
import GranularityDropdown from "./GranularityDropdown";
import CommonLoader from "../../../common_components/CommonLoader";
import moment from "moment";
import ChannelTabs from "../../../common_components/ChannelTabs";
import exportPerformanceReport from "./exportPerformanceReport";
import { NETWORK_ICON_MAP } from "../../../utils/networkConstants";
import LastUpdatedInfo from "../../../common_components/LastUpdatedInfo";
const ClientPerformanceTable = ({ data, loading, globalDateRange, onGlobalDateRangeChange, stickyHeader = false }) => {
    const [isCompareOn, setIsCompareOn] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        columnId: null,
        direction: "asc"
    });
    const [shouldScrollToRow, setShouldScrollToRow] = useState(false);
    // State to track which rows show Value instead of Percentage for Difference column
    const [differenceModes, setDifferenceModes] = useState({});
    const toggleDifferenceMode = (rowId) => {
        setDifferenceModes(prev => ({
            ...prev,
            [rowId]: !prev[rowId]
        }));
    };
    // ✅ Use Custom Hook
    const { expandedRowState, handleDateRangeTabChange, handleDateApply, handleGranularityChange, handleGroupChange, handleEntityToggle, toggleViewMode, setRowActiveTab, getRowState, initializeRowFromGlobal, // 👈 NEW from hook
    syncExpandedRowWithGlobalRange, handleClientRefresh, } = useClientRowState(data);
    const columns = [
        {
            id: "name",
            label: "Client List",
            width: "60%",
            sortable: true,
            draggable: false,
            align: "left",
        },
        {
            id: "revenue",
            label: "Revenue",
            width: "20%",
            sortable: true,
            draggable: false,
            align: "center",
        },
        {
            id: "difference",
            label: "Difference",
            width: "20%",
            sortable: true,
            draggable: false,
            align: "center", // ✅
        },
    ];
    const renderCell = (row, column) => {
        switch (column.id) {
            case "name":
                const normalize = (str) => (str || "").toLowerCase().trim();
                // ✅ Priority: Shopify > GA > Paid (Meta + Adword + Bing)
                let hasShopifyIcon = false;
                let hasGaIcon = false;
                let hasMetaIcon = false;
                let hasGoogleIcon = false;
                let hasBingIcon = false;
                const channelList = row.channel || [];
                channelList.forEach((channel) => {
                    const mc = normalize(channel);
                    if (mc.includes('shopify'))
                        hasShopifyIcon = true;
                    if (mc.includes('ga') || mc.includes('analytics'))
                        hasGaIcon = true;
                    if (mc.includes('meta') || mc.includes('facebook'))
                        hasMetaIcon = true;
                    if (mc.includes('adword') || mc.includes('google'))
                        hasGoogleIcon = true;
                    if (mc.includes('bing'))
                        hasBingIcon = true;
                });
                // Priority: if Shopify or GA is present, don't also show paid icons
                if (hasShopifyIcon) {
                    hasGaIcon = hasMetaIcon = hasGoogleIcon = hasBingIcon = false;
                }
                else if (hasGaIcon) {
                    hasMetaIcon = hasGoogleIcon = hasBingIcon = false;
                }
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }, onClick: (e) => {
                        e.stopPropagation();
                        handleExpandToggle(row._id || row.id);
                    }, children: [row.id !== "all_clients" && (_jsxs("div", { style: { display: "flex" }, children: [hasShopifyIcon && (_jsx("img", { src: NETWORK_ICON_MAP.shopify, style: {
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        backgroundColor: "#ECFDF3",
                                        padding: 2,
                                    } })), hasGaIcon && (_jsx("img", { src: NETWORK_ICON_MAP.ga, style: {
                                        width: 24,
                                        height: 24,
                                        alignSelf: "center",
                                    } })), hasGoogleIcon && (_jsx("img", { src: NETWORK_ICON_MAP.adword, style: {
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        backgroundColor: "#EEF4FF",
                                        padding: 5,
                                    } })), hasMetaIcon && (_jsx("img", { src: NETWORK_ICON_MAP.meta, style: {
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        backgroundColor: "#ecfbfdff",
                                        padding: 5,
                                        position: "relative",
                                        left: hasGoogleIcon ? -10 : 0,
                                    } })), hasBingIcon && (_jsx("img", { src: NETWORK_ICON_MAP.bing, style: {
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        backgroundColor: "#FEF3F2",
                                        padding: 5,
                                        position: "relative",
                                        left: (hasGoogleIcon || hasMetaIcon) ? -10 : 0,
                                    } }))] })), _jsx(Typography, { sx: { fontSize: "14px !important", fontWeight: 400 }, children: row.name === "All Clients" ? "All Client" : row.name })] }));
            case "revenue": {
                const revenue = row.overall_stats?.total_revenue || 0;
                const projected = Number(row.projectedRevenue ?? 0);
                const variance = Number(row.projectedVariance ?? 0);
                const isVarPositive = variance >= 0;
                const roundedVar = Math.round(Math.abs(variance));
                return (_jsxs(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        whiteSpace: "nowrap",
                    }, children: [_jsxs(Box, { sx: { display: "flex", flexDirection: "column", alignItems: "flex-start" }, children: [_jsx(Typography, { children: _jsx(StyledCurrency, { className: "valueclassclient", value: formatStat(revenue, "revenue") }) }), _jsx(Typography, { sx: { fontSize: "12px", lineHeight: 1.2 }, children: _jsx(StyledCurrency, { className: "valueclass2", value: formatStat(projected, "revenue") }) })] }), _jsxs(Box, { sx: {
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "2px",
                                px: 2,
                                py: "6px",
                                borderRadius: "9999px",
                                bgcolor: isVarPositive ? "#dcfce7" : "#fee2e2",
                                color: isVarPositive ? "#16a34a" : "#dc2626",
                                fontSize: "13px",
                                fontWeight: 500,
                                lineHeight: 1,
                            }, children: [roundedVar, "%", isVarPositive
                                    ? _jsx(TrendingUpIcon, { sx: { fontSize: 14 }, className: "iconUp" })
                                    : _jsx(TrendingDownIcon, { sx: { fontSize: 14 }, className: "iconDown" })] })] }));
            }
            case "difference":
                const diffPercent = row.differenceChange || 0;
                const diffValue = row.differenceValue || 0;
                const isDiffPositive = diffPercent >= 0;
                // Toggle state logic
                const isValueMode = differenceModes[row.id];
                return (_jsx(Box, { className: "", sx: {
                        display: "flex",
                        // justifyContent: "center",
                        cursor: "pointer",
                        // bgcolor: isDiffPositive ? "#dcfce7" : "#fee2e2",
                        userSelect: "none"
                    }, onClick: (e) => {
                        e.stopPropagation(); // Prevent row expand
                        toggleDifferenceMode(row.id);
                    }, children: _jsxs(Box, { sx: {
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.5,
                            py: "4px",
                            minWidth: "80px",
                            justifyContent: "center",
                            borderRadius: "9999px",
                            bgcolor: isDiffPositive ? "#dcfce7" : "#fee2e2",
                            color: isDiffPositive ? "#16a34a" : "#dc2626",
                            fontSize: "13px",
                            fontWeight: 500,
                            transition: "all 0.2s"
                        }, children: [isValueMode
                                ? _jsx(StyledCurrency, { symbolColor: isDiffPositive ? "#16a34a" : "#dc2626", className: "valueclassclient2", value: formatStat(diffValue, "revenue") }) // Show as Currency
                                : `${Math.round(Math.abs(diffPercent))}%`, isDiffPositive ? (_jsx(TrendingUpIcon, { className: "iconUp", sx: { fontSize: "14px", color: "#16a34a" } })) : (_jsx(TrendingDownIcon, { className: "iconDown", sx: { fontSize: "14px", color: "#dc2626" } }))] }) }));
        }
    };
    const renderExpandedContent = (row) => {
        const normalize = (str) => (str || "").toLowerCase().trim();
        const normalizedName = normalize(row.name || "");
        const canShowPerformance = ["urban savage", "pro standard"].includes(normalizedName);
        const rowId = row._id || row.id;
        const currentState = getRowState(rowId, row);
        const isPerformanceReport = currentState.viewMode === 'performance_report';
        const dataToUse = currentState.fetchedData || row;
        // ✅ Detect available networks
        const hasShopify = dataToUse.networks?.some((n) => normalize(n.network) === "shopify");
        const hasGa = dataToUse.networks?.some((n) => ["ga", "analytics", "google analytics"].includes(normalize(n.network)));
        const hasMeta = dataToUse.networks?.some((n) => ["meta", "facebook", "facebook ads"].includes(normalize(n.network)));
        const hasGoogle = dataToUse.networks?.some((n) => ["adword", "google ads", "google_ads", "googleads"].includes(normalize(n.network)));
        const hasBing = dataToUse.networks?.some((n) => ["bing", "bing ads"].includes(normalize(n.network)));
        // ✅ Build tabs
        const effectiveHasBing = hasBing && !isPerformanceReport;
        const paidCount = [hasMeta, hasGoogle, effectiveHasBing].filter(Boolean).length;
        const hasAllChannelsData = dataToUse.networks?.some((n) => normalize(n.network) === "all channels");
        const showAllChannels = hasAllChannelsData || hasShopify || hasGa || paidCount >= 2;
        const tabItems = [
            ...(showAllChannels ? [{ key: "All Channels", label: "All Channels" }] : []),
            ...(hasMeta ? [{ key: "meta", label: "Meta Ads" }] : []),
            ...(hasGoogle ? [{ key: "adword", label: "Google Ads" }] : []),
            ...(effectiveHasBing ? [{ key: "bing", label: "Bing Ads" }] : []),
        ];
        // ✅ Safe active tab — fallback to first available if current is invalid
        const isCurrentTabValid = tabItems.some((t) => normalize(t.key) === normalize(currentState.activeTab));
        const activeTabSafe = isCurrentTabValid
            ? currentState.activeTab
            : (tabItems[0]?.key || "All Channels");
        // ✅ Now use activeTabSafe everywhere
        const networkData = dataToUse.networks?.find((net) => normalize(net.network) === normalize(activeTabSafe));
        // ✅ Use monthly_stats for standard ranges on All Channels tab (matches top-row values)
        const STATS_KEY_MAP = {
            today: 'today',
            yesterday: 'yesterday',
            last7days: '7days',
            last30days: '30days',
        };
        const isAllChannelsTab = normalize(currentState.activeTab) === 'all channels';
        const statsKey = STATS_KEY_MAP[currentState.selectedRange];
        let networkDataOverride = networkData;
        // if (isAllChannelsTab && statsKey && dataToUse.monthly_stats) {
        //     const msRevenue = dataToUse.monthly_stats[statsKey];
        //     const msDiff = dataToUse.monthly_stats?.difference?.[statsKey];
        //     if (msRevenue != null) {
        //         networkDataOverride = {
        //             ...networkData,
        //             total_revenue: msRevenue,
        //             previous_total_revenue: msDiff?.value ?? networkData?.previous_total_revenue ?? null,
        //         };
        //     }
        // }
        const isComparisonActive = !!currentState.compareStartDate && !!currentState.compareEndDate;
        const getChartSeries = (chartData, activeTab, isCompareOn = false) => {
            const tab = normalize(activeTab);
            // ✅ Choose which series to use based on compare mode
            const seriesToUse = isCompareOn && chartData.comparison_series
                ? chartData.comparison_series
                : chartData.series;
            const series = [
                seriesToUse?.revenue && {
                    name: "Revenue",
                    data: seriesToUse.revenue,
                    color: "#8C1D5B",
                },
                seriesToUse?.spend && {
                    name: "Spend",
                    data: seriesToUse.spend,
                    color: "#5F8F6B",
                },
                seriesToUse?.roas && {
                    name: "ROAS",
                    data: seriesToUse.roas,
                    color: "#7A5BB6",
                    yAxisIndex: 1,
                },
                seriesToUse?.cpc && {
                    name: "CPC",
                    data: seriesToUse.cpc,
                    color: "#f59e0b",
                    yAxisIndex: 1,
                },
            ];
            // Tab-specific metrics
            if (tab === 'all channels' && seriesToUse?.sessions) {
                series.push({
                    name: "Sessions",
                    data: seriesToUse.sessions,
                    color: "#6FAFB0",
                });
            }
            if (tab === 'meta' && seriesToUse?.outbound_clicks) {
                series.push({
                    name: "Outbound Clicks",
                    data: seriesToUse.outbound_clicks,
                    color: "#14b8a6",
                });
            }
            if ((tab === 'adword' || tab === 'google ads') && seriesToUse?.clicks) {
                series.push({
                    name: "Clicks",
                    data: seriesToUse.clicks,
                    color: "#0ea5e9",
                });
            }
            return series.filter(Boolean);
        };
        const buildComparison = (chartData, tab) => {
            // ✅ FIX: Ensure we're getting the data correctly
            const comparisonSeries = chartData?.comparison_series;
            if (!comparisonSeries || !chartData?.comparison_labels) {
                return {
                    labels: [],
                    series: {}
                };
            }
            const base = {
                revenue: comparisonSeries.revenue || [],
                spend: comparisonSeries.spend || [],
                roas: comparisonSeries.roas || [],
                cpc: comparisonSeries.cpc || [],
            };
            const normalizedTab = tab.toLowerCase().trim();
            if (normalizedTab === "all channels") {
                return {
                    labels: chartData.comparison_labels || [],
                    series: {
                        ...base,
                        sessions: comparisonSeries.sessions || [],
                    },
                };
            }
            if (normalizedTab === "meta") {
                return {
                    labels: chartData.comparison_labels || [],
                    series: {
                        ...base,
                        outbound_clicks: comparisonSeries.outbound_clicks || [],
                    },
                };
            }
            if (normalizedTab === "adword" || normalizedTab === "google ads") {
                return {
                    labels: chartData.comparison_labels || [],
                    series: {
                        ...base,
                        clicks: comparisonSeries.clicks || [],
                    },
                };
            }
            return {
                labels: chartData.comparison_labels || [],
                series: base,
            };
        };
        const isUrbanSavage = normalizedName === "urban savage";
        const isProStandard = normalizedName === "pro standard";
        const normalizedRange = currentState.selectedRange;
        const isAnyLoading = (!isPerformanceReport && (currentState.isLoadingData || currentState.isRefreshingClient)) ||
            (isPerformanceReport && (currentState.isLoadingPerformance || currentState.isRefreshingClient));
        return (_jsxs(Box, { "data-expanded-row-id": rowId, sx: {
                p: 1,
                bgcolor: "white",
                borderTop: "1px solid #f1f5f9",
                borderBottom: "1px solid #f1f5f9",
                borderRadius: "8px"
            }, children: [_jsx(Box, { sx: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                        width: '100%'
                    }, children: _jsxs(Box, { sx: { width: '100%', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, border: "1px solid #ddd", padding: "8px", borderRadius: "8px" }, children: [_jsx(DateRangeTabs, { selectedRange: normalizedRange, mode: isPerformanceReport ? "performance" : "account", disabled: isAnyLoading, onChange: (range) => {
                                    const currentRange = currentState.selectedRange;
                                    if (currentRange === range)
                                        return;
                                    // ✅ Set flag to scroll after data loads
                                    setShouldScrollToRow(true);
                                    if (isPerformanceReport) {
                                        handleGranularityChange(rowId, range === "day" ? "daily" :
                                            range === "week" ? "weekly" :
                                                "monthly");
                                    }
                                    else {
                                        handleDateRangeTabChange(rowId, range);
                                    }
                                } }), canShowPerformance && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(Typography, { sx: {
                                            fontSize: '13px',
                                            fontWeight: !isPerformanceReport ? 700 : 500,
                                            color: !isPerformanceReport ? '#0f172a' : '#64748b',
                                            transition: 'color 0.2s',
                                            cursor: isAnyLoading ? 'not-allowed' : 'pointer'
                                        }, onClick: () => !isAnyLoading && !isPerformanceReport && toggleViewMode(rowId), children: "Account Summary" }), _jsx(Box, { className: "compare-toggle-container", children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: isPerformanceReport, disabled: isAnyLoading, onChange: () => toggleViewMode(rowId), className: "compare-switch" }), label: "", className: "compare-label" }) }), _jsx(Typography, { sx: {
                                            fontSize: '13px',
                                            fontWeight: isPerformanceReport ? 700 : 500,
                                            color: isPerformanceReport ? '#0f172a' : '#64748b',
                                            transition: 'color 0.2s',
                                            cursor: isAnyLoading ? 'not-allowed' : 'pointer'
                                        }, onClick: () => !isAnyLoading && (isPerformanceReport || toggleViewMode(rowId)), children: "Performance Report" })] }))] }) }), _jsx(Box, { sx: { width: '100%', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, border: "1px solid #ddd", padding: "8px", borderRadius: "8px" }, children: _jsxs(Box, { sx: {
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: 'wrap',
                            gap: 1,
                        }, children: [_jsxs("div", { style: { display: 'flex', flexDirection: "column", gap: "8px", padding: "4px 0" }, children: [!isPerformanceReport && row.id !== "all_clients" && currentState.hasCustomRange && (_jsx(Box, { sx: { flexShrink: 0 }, children: _jsx(LastUpdatedInfo, { updatedAt: currentState.lastUpdated, onRefresh: () => handleClientRefresh(rowId), loading: currentState.isRefreshingClient, disabled: isAnyLoading, showIcon: true, showAgo: true }) })), _jsxs(Box, { sx: { display: 'flex', width: "100%", gap: 1, alignItems: 'center' }, children: [row.id !== "all_clients" && (_jsx(Box, { sx: { flexShrink: 0 }, children: _jsx(CustomDatePicker, { disabled: isAnyLoading, value: currentState.startDate && currentState.endDate
                                                        ? { start: currentState.startDate, end: currentState.endDate }
                                                        : null, compareValue: currentState.compareStartDate && currentState.compareEndDate
                                                        ? { start: currentState.compareStartDate, end: currentState.compareEndDate }
                                                        : null, onApply: (data) => {
                                                        if (data.mainRange && data.mainRange.start && data.mainRange.end) {
                                                            handleDateApply(rowId, data.mainRange, data.compareRange);
                                                        }
                                                    }, comparison: true, placeholder: "Select Date Range", comparePlaceholder: "Compare Range" }) })), !isPerformanceReport && (_jsx(GranularityDropdown, { disabled: isAnyLoading, startDate: currentState.startDate, endDate: currentState.endDate, granularity: currentState.granularity || 'daily', onChange: (value) => handleGranularityChange(rowId, value) }))] })] }), isPerformanceReport && (_jsx(FiltersRow, { selectedLeague: currentState.selectedGroup || "All", leagueOptions: [
                                    { label: "All", value: "All" },
                                    ...(currentState.entitiesData?.map((g) => ({
                                        label: g.group_name,
                                        value: g.group_name,
                                    })) || []),
                                ], leagueLabel: isUrbanSavage ? "Product Type" : "League", teamLabel: isProStandard ? "Team" : undefined, onLeagueChange: (val) => handleGroupChange(rowId, val), renderTeamSelect: () => (currentState.selectedGroup !== "All" ? (_jsx(Box, { children: _jsx(VisibleClientsDropdown, { showIcon: false, buttonWidth: 120, adjustWidth: 130, label: isProStandard ? "Team" : "Product", itemName: "teams", enableUpdateButton: true, clients: currentState.entityList || [], onItemToggle: async () => { }, onChange: (updated) => handleEntityToggle(rowId, updated) }) })) : null), onExport: () => {
                                    if (!currentState.performanceData)
                                        return;
                                    const fmt = (d) => d ? `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}` : undefined;
                                    exportPerformanceReport(currentState.performanceData, {
                                        clientName: row.name || row.client_name || rowId,
                                        startDate: fmt(currentState.startDate),
                                        endDate: fmt(currentState.endDate),
                                        compareStartDate: fmt(currentState.compareStartDate),
                                        compareEndDate: fmt(currentState.compareEndDate)
                                    });
                                }, onCopy: () => console.log("Copy/Save clicked") }))] }) }), _jsx(Box, { sx: { minHeight: "500px", position: "relative" }, children: isAnyLoading ? (_jsx(Box, { sx: {
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "500px",
                            width: "100%"
                        }, children: _jsx(CommonLoader, { loaderClass: "custom-loader-class", containerClassName: "embedded-loader-container expand custom-loader-container" }) })) : (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 1, my: 1, border: "1px solid #e5e7eb", borderRadius: "8px", padding: "0px" }, children: [(() => {
                                const hasShopify = dataToUse.networks?.some((n) => normalize(n.network) === "shopify");
                                const hasGa = dataToUse.networks?.some((n) => ["ga", "analytics", "google analytics"].includes(normalize(n.network)));
                                const hasMeta = dataToUse.networks?.some((n) => ["meta", "facebook", "facebook ads"].includes(normalize(n.network)));
                                const hasGoogle = dataToUse.networks?.some((n) => ["adword", "google ads", "google_ads", "googleads"].includes(normalize(n.network)));
                                const hasBing = dataToUse.networks?.some((n) => ["bing", "bing ads"].includes(normalize(n.network)));
                                // ✅ All Channels tab visibility:
                                // - Show if Shopify or GA connected (always)
                                // - Show if 2+ paid channels (meta/adword/bing) connected
                                const effectiveHasBing = hasBing && !isPerformanceReport;
                                const paidCount = [hasMeta, hasGoogle, effectiveHasBing].filter(Boolean).length;
                                const showAllChannels = hasShopify || hasGa || paidCount >= 2;
                                const tabItems = [
                                    ...(showAllChannels ? [{ key: "All Channels", label: "All Channels" }] : []),
                                    ...(hasMeta ? [{ key: "meta", label: "Meta Ads" }] : []),
                                    ...(hasGoogle ? [{ key: "adword", label: "Google Ads" }] : []),
                                    ...(effectiveHasBing ? [{ key: "bing", label: "Bing Ads" }] : []),
                                ];
                                // ✅ Determine safe active tab
                                let activeTabSafe = currentState.activeTab;
                                const isCurrentTabValid = tabItems.some((t) => normalize(t.key) === normalize(currentState.activeTab));
                                if (!isCurrentTabValid && tabItems.length > 0) {
                                    // Fallback: first available tab
                                    activeTabSafe = tabItems[0].key;
                                }
                                return (_jsx(ChannelTabs, { tabs: tabItems, value: activeTabSafe, onChange: (key) => setRowActiveTab(rowId, key) }));
                            })(), _jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 1 }, children: (() => {
                                    return (_jsxs(_Fragment, { children: [isPerformanceReport ? (_jsx(SportPerformance, { data: currentState.performanceData, entities: currentState.entitiesData, loading: !!currentState.isLoadingPerformance, activeTab: currentState.activeTab || "All Channels", clientId: rowId })) : (_jsx(_Fragment, { children: !isPerformanceReport && (networkDataOverride ? (_jsx(ClientStatsGrid, { networkData: networkDataOverride, isComparisonActive: isComparisonActive, activeTab: currentState.activeTab, row: dataToUse })) : (_jsx(Typography, { sx: { textAlign: "center", py: 4 }, children: "No data available" }))) })), !isPerformanceReport &&
                                                networkData?.chart_data && (_jsx(Box, { children: _jsx(AnalyticsLineChart, { onCompareToggle: setIsCompareOn, categories: networkData.chart_data.labels, series: getChartSeries(networkData.chart_data, currentState.activeTab), height: 320, showLegend: true, 
                                                    // ✅ NEW: Pass hour cutoff
                                                    hourCutoff: (() => {
                                                        // Only apply cutoff if end date is today
                                                        const endMoment = moment(currentState.endDate);
                                                        const today = moment().startOf('day');
                                                        if (endMoment.isSame(today, 'day')) {
                                                            return moment().hours(); // Current hour (0-23)
                                                        }
                                                        return null; // No cutoff for past dates
                                                    })(), endDate: currentState.endDate
                                                        ? moment(currentState.endDate).format('YYYY-MM-DD')
                                                        : undefined, comparison: buildComparison(networkData?.chart_data, normalize(currentState.activeTab)), legendVariant: "pills" }) }))] }));
                                })() })] })) })] }));
    };
    const [expandedRowId, setExpandedRowId] = useState(null);
    // Sync expanded row when global date range changes
    // Also scroll back to the expanded row after re-sort (data shifts position)
    React.useEffect(() => {
        if (expandedRowId) {
            syncExpandedRowWithGlobalRange(expandedRowId, globalDateRange);
            // Re-sort will move the row — scroll to it after data settles
            setShouldScrollToRow(true);
        }
    }, [globalDateRange]);
    const [columnFilters, setColumnFilters] = useState({});
    const [showFilterRow, setShowFilterRow] = useState(false);
    const [activeFilterColumnId, setActiveFilterColumnId] = useState(null);
    const handleSortChange = (columnId, direction) => {
        setSortConfig({
            columnId,
            direction: direction || "asc"
        });
    };
    const handleExpandToggle = (rowId) => {
        setExpandedRowId(prev => {
            const isExpanding = prev !== rowId;
            if (isExpanding) {
                initializeRowFromGlobal(rowId, globalDateRange);
            }
            return isExpanding ? rowId : null;
        });
    };
    const handleColumnFilterChange = (columnId, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [columnId]: value,
        }));
    };
    const columnFilterConfig = {
        enable: true,
        columns: {
            name: { type: "search" },
            revenue: { type: "search" },
            difference: { type: "search" },
        },
    };
    const filteredData = useMemo(() => {
        const realClients = data?.filter((d) => d.id !== "all_clients");
        if (realClients.length === 0)
            return [];
        return data;
    }, [data]);
    const sortedData = useMemo(() => {
        if (!sortConfig.columnId)
            return filteredData;
        // ⭐ Separate All Clients Row
        const allClientRow = filteredData.find(r => r.id === "all_clients");
        const otherRows = filteredData.filter(r => r.id !== "all_clients");
        // ⭐ Sort Only Other Rows
        const sortedOthers = [...otherRows].sort((a, b) => {
            let aVal, bVal;
            switch (sortConfig.columnId) {
                case "name":
                    aVal = a.name?.toLowerCase() || "";
                    bVal = b.name?.toLowerCase() || "";
                    break;
                case "revenue":
                    aVal = a.overall_stats?.total_revenue || 0;
                    bVal = b.overall_stats?.total_revenue || 0;
                    break;
                case "difference":
                    aVal = a.differenceChange || 0;
                    bVal = b.differenceChange || 0;
                    break;
                default:
                    return 0;
            }
            if (aVal < bVal)
                return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal)
                return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
        // ⭐ Always Put All Clients On Top
        return allClientRow ? [allClientRow, ...sortedOthers] : sortedOthers;
    }, [filteredData, sortConfig]);
    useEffect(() => {
        if (shouldScrollToRow && expandedRowId) {
            const timer = setTimeout(() => {
                // Find the expanded content box
                const expandedContent = document.querySelector(`[data-expanded-row-id="${expandedRowId}"]`);
                if (expandedContent) {
                    // Walk up to find the <tr> that holds this expanded content row,
                    // then go one more sibling back to get the actual data row TR
                    const expandedTR = expandedContent.closest('tr');
                    const rowTR = expandedTR?.previousElementSibling;
                    const target = rowTR || expandedContent;
                    // Scroll within our custom scroll container
                    const scrollContainer = document.querySelector('.account-summary-scroll-body');
                    if (scrollContainer && target) {
                        const containerRect = scrollContainer.getBoundingClientRect();
                        const targetRect = target.getBoundingClientRect();
                        const offset = targetRect.top - containerRect.top + scrollContainer.scrollTop - 115; // 115px = tabs + thead height
                        scrollContainer.scrollTo({ top: offset, behavior: 'smooth' });
                    }
                    else if (target) {
                        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                }
                setShouldScrollToRow(false);
            }, 300); // allow re-sort to settle before measuring
            return () => clearTimeout(timer);
        }
    }, [sortedData, shouldScrollToRow, expandedRowId]);
    return (_jsx(_Fragment, { children: _jsx(CommonTable, { columns: columns, rows: sortedData, 
            // isLoading={loading}
            renderCell: renderCell, renderExpandedContent: renderExpandedContent, expandTrigger: EXPAND_TRIGGERS.ICON, onExpandToggle: handleExpandToggle, controlledExpandedRows: expandedRowId ? new Set([expandedRowId]) : new Set(), sortConfig: sortConfig, onSortChange: handleSortChange, columnFilterConfig: columnFilterConfig, enableColumnActions: true, enableColumnFilters: false, enableColumnReorder: true, showFilterRow: showFilterRow, activeFilterColumnId: activeFilterColumnId, onFilterRowToggle: (payload) => {
                if (!payload || payload.mode === "all") {
                    setActiveFilterColumnId(null);
                    setShowFilterRow(prev => !prev);
                    return;
                }
                if (payload.mode === "single" && payload.columnId) {
                    setActiveFilterColumnId(payload.columnId);
                    setShowFilterRow(true);
                }
            }, columnFilters: columnFilters, onColumnFilterChange: handleColumnFilterChange, tableHeadClassName: "client-performance-table-head", expandCellClassName: "reusable-table-header-cell reusable-table-header-cell-expand account-summary-table-expand-cell", stickyHeader: stickyHeader }) }));
};
export default ClientPerformanceTable;
